"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { WaitlistModal } from "@/components/waitlist-modal";
import { Watch, Megaphone } from "lucide-react";

export default function GetStartedPage() {
  const [showIosModal, setShowIosModal] = useState(false);
  const [showWatchModal, setShowWatchModal] = useState(false);

  return (
    <div className="animate-in fade-in duration-500">
      <WaitlistModal 
        isOpen={showIosModal} 
        onClose={() => setShowIosModal(false)} 
        title="Coming Soon to iOS"
        description="We are currently working hard on the iPhone version of MVPlayer. Join the waitlist to get notified as soon as it's ready!"
        type="ios_waitlist"
      />
      <WaitlistModal 
        isOpen={showWatchModal} 
        onClose={() => setShowWatchModal(false)} 
        title="More Watches Coming Soon"
        description="We are expanding support to Apple Watch, Galaxy Watch, Google Pixel and Fitbit Watch. Join the waitlist to get notified as soon as they're supported!"
        type="watch_waitlist"
        showDropdown={true}
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="text-center mb-4">
          <h1 
            className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.6s forwards",
            }}
          >
            Get Started
          </h1>
          
          <div className="mt-8 space-y-2 text-center opacity-0 blur-md translate-y-8"
               style={{ animation: "fadeSlideUnblur 1s ease-out 0.8s forwards" }}>
            <p className="flex items-center justify-center gap-2 text-[#C4C8BA] text-sm sm:text-base">
              <span className="inline-block">
                <Image src="/drawables/sports_tennis_24dp_1F1F1F_FILL0_wght400_GRAD0_opsz24.svg" alt="Tennis" width={16} height={16} className="inline-block w-4 h-4" />
              </span> Score Padel, Tennis & Badminton matches
            </p>
            <p className="flex items-center justify-center gap-2 text-[#C4C8BA] text-sm sm:text-base">
              <Watch className="inline-block w-4 h-4 text-[#B0D18B]" /> Control the Game from Your Wrist
            </p>
            <p className="flex items-center justify-center gap-2 text-[#C4C8BA] text-sm sm:text-base">
              <span className="inline-block">
                <Image src="/drawables/mobile_speaker_24dp_1F1F1F_FILL0_wght400_GRAD0_opsz24.svg" alt="Speaker" width={16} height={16} className="inline-block w-4 h-4" />
              </span> Connect Up to 4 Devices
            </p>
          </div>
        </div>
        
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-center opacity-0 blur-md translate-y-8 mb-12"
               style={{ animation: "fadeSlideUnblur 1s ease-out 0.8s forwards" }}>
              <Image 
                src="/drawables/scoreboard_preview.webp" 
                alt="MVPlayer Scoreboard Preview" 
                width={300} 
                height={300} 
                className="h-auto w-full max-w-[280px] object-contain drop-shadow-2xl"
              />
          </div>

          <div 
            className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-200 relative shadow-[0_0_30px_rgba(176,209,139,0.05)]"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.8s forwards",
            }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              Step 1
            </div>
            <h3 className="text-[#E2E3D8] font-semibold font-sriracha mb-2 text-lg">
              Smartphone
            </h3>
            <p className="text-[#C4C8BA] text-sm leading-relaxed">
              Download the MVPlayer app on your smartphone to start scoring games.
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
                <a href="https://play.google.com/store/apps/details?id=eu.mvplayer.mvplayer" target="_blank" rel="noopener noreferrer">
                    <Image src="/drawables/google-play-badge.svg" alt="Google Play" width={161} height={48} className="h-12 w-auto"/>
                </a>
                <Image src="/drawables/download-on-the-app-store-badge.svg" alt="App Store" width={162} height={48} className="h-12 w-auto"/>
            </div>
          </div>

          <div 
            className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-200 relative shadow-[0_0_30px_rgba(176,209,139,0.05)]"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.8s forwards",
            }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              Step 2
            </div>
            <h3 className="text-[#E2E3D8] font-semibold font-sriracha mb-2 text-lg">
              Smartwatch
            </h3>
            <p className="text-[#C4C8BA] text-sm leading-relaxed">
              MVPlayer is best played with a smartwatch. We support <strong className="text-[#B0D18B]">Garmin</strong>, <strong className="text-[#B0D18B]">WearOS</strong> and <strong className="text-[#B0D18B]">Apple Watch</strong>.
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
                <a href="https://apps.garmin.com/en-US/apps/d783531d-9a68-4e2f-b81c-29a776a55c85" target="_blank" rel="noopener noreferrer">
                    <Image src="/drawables/available-connect-iq-badge.svg" alt="Connect IQ" width={229} height={48} className="h-12 w-auto"/>
                </a>
                <Image src="/drawables/WearOS.svg" alt="Wear OS" width={162} height={48} className="h-12 w-auto"/>
                <Image src="/drawables/Apple_WATCH.svg" alt="Apple Watch" width={162} height={48} className="h-12 w-auto"/>
            </div>
          </div>

          <div 
            className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-200"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.8s forwards",
            }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              Step 3
            </div>
            <h3 className="text-[#E2E3D8] font-semibold font-sriracha mb-2 text-lg">
              You play, We score!
            </h3>
            <p className="text-[#C4C8BA] text-sm leading-relaxed">
              Go play your game of <Link href="/padel" className="text-[#B0D18B] hover:underline">Padel</Link>, <Link href="/tennis" className="text-[#B0D18B] hover:underline">Tennis</Link> or <Link href="/badminton" className="text-[#B0D18B] hover:underline">Badminton</Link>. More sports will be added soon.
            </p>
            <p className="text-[#C4C8BA] text-sm leading-relaxed">
              Follow the links for detailed instructions for your favorite game.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}